/**
 * ============================================================
 * HOBBLIVISION OCR PREPROCESSOR
 * ============================================================
 *
 * Prepares already-detected card regions for OCR.
 *
 * IMPORTANT:
 * This runs AFTER:
 *
 *   detector
 *      ↓
 *   ROI
 *      ↓
 *   cardRegions
 *
 * It does NOT perform collectible detection.
 *
 * Goals:
 * - Preserve original region
 * - Upscale small text regions
 * - Create grayscale representation
 * - Create enhanced grayscale representation
 * - Keep preprocessing conservative for V1
 *
 * Future possibilities:
 * - sharpening
 * - adaptive thresholding
 * - denoising
 * - perspective correction
 * - region-specific preprocessing
 * ============================================================
 */

const OCR_PREPROCESSOR_VERSION = "2.0";

const CONFIG = {
    /**
     * Minimum useful width for OCR.
     *
     * Small label/certification regions can benefit
     * significantly from being enlarged before OCR.
     */
    MIN_OCR_WIDTH: 800,

    /**
     * Maximum upscale factor.
     *
     * We don't want to create enormous canvases.
     */
    MAX_UPSCALE: 3,

    /**
     * Contrast multiplier for enhanced grayscale.
     *
     * 1.0 = unchanged
     * >1 = increased contrast
     */
    CONTRAST: 1.25,

    /**
     * Brightness adjustment.
     *
     * 0 = unchanged.
     */
    BRIGHTNESS: 0
};

/**
 * ============================================================
 * PUBLIC API
 * ============================================================
 *
 * Preprocess all detected OCR regions.
 *
 * @param {Object} regions
 * Output from extractRegionsFromROI()
 *
 * @returns {Object}
 * {
 *   version,
 *   regions: {
 *      label: {...},
 *      card: {...},
 *      certification: {...}
 *   }
 * }
 */
export function preprocessOCRRegions(
    regions = {}
) {
    const processedRegions = {};

    Object.entries(regions).forEach(
        ([name, region]) => {
            if (!region?.canvas) {
                console.warn(
                    `HobbliVision OCR: Region "${name}" has no canvas.`
                );
                return;
            }

            processedRegions[name] =
                preprocessOCRRegion(
                    region,
                    name
                );
        }
    );

    return {
        version:
            OCR_PREPROCESSOR_VERSION,

        regions:
            processedRegions,

        createdAt:
            new Date().toISOString()
    };
}

/**
 * ============================================================
 * PROCESS SINGLE REGION
 * ============================================================
 */
export function preprocessOCRRegion(
    region,
    name = "unknown"
) {
    const originalCanvas =
        region.canvas;

    const originalWidth =
        originalCanvas.width;

    const originalHeight =
        originalCanvas.height;

    /**
     * Determine whether the region needs enlargement.
     */
    const upscaleFactor =
        calculateUpscaleFactor(
            originalWidth
        );

    /**
     * Create normalized/upscaled image.
     */
    const resizedCanvas =
        resizeForOCR(
            originalCanvas,
            upscaleFactor
        );

    /**
     * Create grayscale representation.
     */
    const grayscale =
        createGrayscaleCanvas(
            resizedCanvas
        );

    /**
     * Create enhanced grayscale representation.
     */
    const enhanced =
        enhanceGrayscale(
            grayscale,
            CONFIG.CONTRAST,
            CONFIG.BRIGHTNESS
        );

    const orientation = Number(region.orientation || 0);
    const rotatedOriginal =
        orientation === 90 || orientation === 270
            ? rotateCanvas(resizedCanvas, orientation)
            : null;

    return {
        name,

        type: region.type || "text",
        confidence: region.confidence || 0,
        textness: region.textness || 0,
        orientation,

        /**
         * Preserve original region metadata.
         */
        source: {
            x: region.x,
            y: region.y,
            width: region.width,
            height: region.height,

            normalized:
                region.normalized
        },

        /**
         * Original region.
         *
         * We deliberately preserve this.
         */
        original: {
            canvas:
                originalCanvas,

            width:
                originalWidth,

            height:
                originalHeight,

            imageUrl:
                canvasToDataURL(
                    originalCanvas
                )
        },

        /**
         * Upscaled region.
         */
        resized: {
            canvas:
                resizedCanvas,

            width:
                resizedCanvas.width,

            height:
                resizedCanvas.height,

            scale:
                upscaleFactor,

            imageUrl:
                canvasToDataURL(
                    resizedCanvas
                )
        },

        /**
         * Grayscale representation.
         */
        grayscale: {
            canvas:
                grayscale,

            width:
                grayscale.width,

            height:
                grayscale.height,

            imageUrl:
                canvasToDataURL(
                    grayscale
                )
        },

        /**
         * Enhanced grayscale.
         */
        enhanced: {
            canvas:
                enhanced,

            width:
                enhanced.width,

            height:
                enhanced.height,

            imageUrl:
                canvasToDataURL(
                    enhanced
                )
        },

        rotatedOriginal: rotatedOriginal
            ? {
                canvas: rotatedOriginal,
                width: rotatedOriginal.width,
                height: rotatedOriginal.height,
                rotation: orientation,
                imageUrl: canvasToDataURL(rotatedOriginal)
            }
            : null
    };
}

/**
 * ============================================================
 * UPSCALE CALCULATION
 * ============================================================
 */
function calculateUpscaleFactor(
    width
) {
    if (
        !width ||
        width >= CONFIG.MIN_OCR_WIDTH
    ) {
        return 1;
    }

    const factor =
        CONFIG.MIN_OCR_WIDTH /
        width;

    return Math.min(
        CONFIG.MAX_UPSCALE,
        Math.max(1, factor)
    );
}

/**
 * ============================================================
 * RESIZE
 * ============================================================
 */
function resizeForOCR(
    sourceCanvas,
    scale
) {
    if (scale <= 1) {
        return cloneCanvas(
            sourceCanvas
        );
    }

    const canvas =
        document.createElement(
            "canvas"
        );

    canvas.width =
        Math.round(
            sourceCanvas.width *
                scale
        );

    canvas.height =
        Math.round(
            sourceCanvas.height *
                scale
        );

    const context =
        canvas.getContext("2d");

    if (!context) {
        throw new Error(
            "HobbliVision OCR: Unable to create resize context."
        );
    }

    /**
     * High-quality browser interpolation.
     */
    context.imageSmoothingEnabled =
        true;

    context.imageSmoothingQuality =
        "high";

    context.drawImage(
        sourceCanvas,
        0,
        0,
        canvas.width,
        canvas.height
    );

    return canvas;
}

/**
 * ============================================================
 * ROTATION
 * ============================================================
 */
function rotateCanvas(sourceCanvas, degrees) {
    const quarterTurn = degrees === 90 || degrees === 270;
    const canvas = document.createElement("canvas");
    canvas.width = quarterTurn ? sourceCanvas.height : sourceCanvas.width;
    canvas.height = quarterTurn ? sourceCanvas.width : sourceCanvas.height;
    const context = canvas.getContext("2d");
    if (!context) return sourceCanvas;

    context.translate(canvas.width / 2, canvas.height / 2);
    context.rotate((degrees * Math.PI) / 180);
    context.drawImage(
        sourceCanvas,
        -sourceCanvas.width / 2,
        -sourceCanvas.height / 2
    );
    return canvas;
}

/**
 * ============================================================
 * GRAYSCALE
 * ============================================================
 */
function createGrayscaleCanvas(
    sourceCanvas
) {
    const canvas =
        cloneCanvas(
            sourceCanvas
        );

    const context =
        canvas.getContext(
            "2d",
            {
                willReadFrequently:
                    true
            }
        );

    if (!context) {
        throw new Error(
            "HobbliVision OCR: Unable to create grayscale context."
        );
    }

    const imageData =
        context.getImageData(
            0,
            0,
            canvas.width,
            canvas.height
        );

    const pixels =
        imageData.data;

    for (
        let i = 0;
        i < pixels.length;
        i += 4
    ) {
        /**
         * Perceptual luminance.
         *
         * Better than simple RGB averaging
         * for text recognition.
         */
        const gray =
            Math.round(
                0.299 * pixels[i] +
                0.587 * pixels[i + 1] +
                0.114 * pixels[i + 2]
            );

        pixels[i] =
            gray;

        pixels[i + 1] =
            gray;

        pixels[i + 2] =
            gray;
    }

    context.putImageData(
        imageData,
        0,
        0
    );

    return canvas;
}

/**
 * ============================================================
 * CONTRAST / BRIGHTNESS ENHANCEMENT
 * ============================================================
 */
function enhanceGrayscale(
    sourceCanvas,
    contrast = 1.25,
    brightness = 0
) {
    const canvas =
        cloneCanvas(
            sourceCanvas
        );

    const context =
        canvas.getContext(
            "2d",
            {
                willReadFrequently:
                    true
            }
        );

    if (!context) {
        throw new Error(
            "HobbliVision OCR: Unable to create enhancement context."
        );
    }

    const imageData =
        context.getImageData(
            0,
            0,
            canvas.width,
            canvas.height
        );

    const pixels =
        imageData.data;

    /**
     * Standard contrast formula.
     */
    const factor =
        (259 * (contrast + 255)) /
        (255 * (259 - contrast));

    for (
        let i = 0;
        i < pixels.length;
        i += 4
    ) {
        let value =
            pixels[i];

        value =
            factor *
                (value - 128) +
            128 +
            brightness;

        value =
            Math.max(
                0,
                Math.min(
                    255,
                    value
                )
            );

        pixels[i] =
            value;

        pixels[i + 1] =
            value;

        pixels[i + 2] =
            value;
    }

    context.putImageData(
        imageData,
        0,
        0
    );

    return canvas;
}

/**
 * ============================================================
 * CANVAS CLONE
 * ============================================================
 */
function cloneCanvas(
    sourceCanvas
) {
    const canvas =
        document.createElement(
            "canvas"
        );

    canvas.width =
        sourceCanvas.width;

    canvas.height =
        sourceCanvas.height;

    const context =
        canvas.getContext("2d");

    if (!context) {
        throw new Error(
            "HobbliVision OCR: Unable to create canvas context."
        );
    }

    context.drawImage(
        sourceCanvas,
        0,
        0
    );

    return canvas;
}

/**
 * ============================================================
 * DATA URL
 * ============================================================
 */
function canvasToDataURL(
    canvas
) {
    try {
        return canvas.toDataURL(
            "image/png"
        );
    } catch (error) {
        console.warn(
            "HobbliVision OCR: Unable to create debug image URL.",
            error
        );

        return null;
    }
}

/**
 * ============================================================
 * DEBUG SUMMARY
 * ============================================================
 *
 * Useful for pipeline logging.
 */
export function summarizeOCRPreprocessing(
    result
) {
    if (!result?.regions) {
        return {
            version:
                OCR_PREPROCESSOR_VERSION,
            regionCount: 0,
            regions: []
        };
    }

    return {
        version:
            result.version,

        regionCount:
            Object.keys(
                result.regions
            ).length,

        regions:
            Object.entries(
                result.regions
            ).map(
                ([name, region]) => ({
                    name,

                    originalWidth:
                        region.original
                            ?.width,

                    originalHeight:
                        region.original
                            ?.height,

                    resizedWidth:
                        region.resized
                            ?.width,

                    resizedHeight:
                        region.resized
                            ?.height,

                    upscale:
                        region.resized
                            ?.scale
                })
            )
    };
}